/**
 * Mixin per gestire il refresh automatico dei dati con controllo della visibilità della pagina.
 *
 * Quando la pagina non è visibile (tab inattiva, finestra minimizzata), il refresh viene sospeso
 * per risparmiare risorse. Quando la pagina torna visibile, il refresh riprende e i dati vengono
 * ricaricati immediatamente.
 *
 * UTILIZZO:
 * 1. Importa il mixin: import autoRefreshMixin from "@/mixins/autoRefreshMixin";
 * 2. Aggiungi ai mixins: mixins: [autoRefreshMixin],
 * 3. Definisci il metodo refreshData() nel tuo componente
 * 4. Configura l'intervallo: this.setupAutoRefresh(30000) // 30 secondi
 *
 * ESEMPIO:
 * export default {
 *   mixins: [autoRefreshMixin],
 *   mounted() {
 *     this.setupAutoRefresh(30000); // Refresh ogni 30 secondi
 *     this.refreshData(); // Carica dati iniziali
 *   },
 *   methods: {
 *     refreshData() {
 *       // Il tuo codice per caricare i dati
 *     }
 *   }
 * }
 */
export default {
    data() {
        return {
            autoRefreshTimer: null,
            autoRefreshInterval: null,
        };
    },

    beforeDestroy() {
        this.stopAutoRefresh();
        this.removeVisibilityListener();
    },

    methods: {
        /**
         * Configura e avvia il refresh automatico
         * @param {number} interval - Intervallo in millisecondi (es. 30000 per 30 secondi)
         */
        setupAutoRefresh(interval) {
            this.autoRefreshInterval = interval;
            this.startAutoRefresh();
            this.addVisibilityListener();
        },

        /**
         * Avvia il timer per il refresh automatico
         */
        startAutoRefresh() {
            if (this.autoRefreshTimer || !this.autoRefreshInterval) {
                return; // Già in esecuzione o non configurato
            }

            this.autoRefreshTimer = setInterval(() => {
                if (!document.hidden) {
                    // Chiama refreshData solo se definito nel componente
                    if (typeof this.refreshData === "function") {
                        this.refreshData();
                    }
                }
            }, this.autoRefreshInterval);
        },

        /**
         * Ferma il refresh automatico
         */
        stopAutoRefresh() {
            if (this.autoRefreshTimer) {
                clearInterval(this.autoRefreshTimer);
                this.autoRefreshTimer = null;
            }
        },

        /**
         * Gestisce il cambio di visibilità della pagina
         */
        handleAutoRefreshVisibilityChange() {
            if (document.hidden) {
                // Pagina nascosta: ferma il refresh
                this.stopAutoRefresh();
                console.log(`📴 ${this.$options.name || "Component"}: refresh automatico sospeso`);
            } else {
                // Pagina visibile: riprendi il refresh
                this.startAutoRefresh();

                // Ricarica subito i dati se refreshData è definito
                if (typeof this.refreshData === "function") {
                    this.refreshData();
                }

                console.log(`✅ ${this.$options.name || "Component"}: refresh automatico ripreso`);
            }
        },

        /**
         * Aggiunge il listener per la visibilità
         */
        addVisibilityListener() {
            document.addEventListener("visibilitychange", this.handleAutoRefreshVisibilityChange);
        },

        /**
         * Rimuove il listener per la visibilità
         */
        removeVisibilityListener() {
            document.removeEventListener("visibilitychange", this.handleAutoRefreshVisibilityChange);
        },
    },
};
